import styled from "@emotion/styled/macro";
import {
  LoadingIndicator,
  MOBILE_BREAKPOINT,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
} from "@sunrun/experience-ui-components";
import { useEffect, useState } from "react";
import { NEW_HOMES_TILED_IFRAME_SRC } from "constants/newHomes";

const IFRAME_SRC = NEW_HOMES_TILED_IFRAME_SRC;

const NewHomesExpandableIframe = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInlineLoading, setIsInlineLoading] = useState(true);

  useEffect(() => {
    const sourceUrl = new URL(IFRAME_SRC);
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = sourceUrl.origin;

    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = sourceUrl.origin;

    document.head.appendChild(preconnect);
    document.head.appendChild(dnsPrefetch);

    return () => {
      document.head.removeChild(preconnect);
      document.head.removeChild(dnsPrefetch);
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [isExpanded]);

  return (
    <>
      {isExpanded && <Overlay onClick={() => setIsExpanded(false)} />}
      <SectionContainer>
        <IframeCard isExpanded={isExpanded}>
          {isExpanded ? (
            <CollapseButton
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label="Collapse new homes content"
            >
              <SvgIcon
                name={SvgNames.CloseFilled}
                width={34}
                height={34}
                color={tokens.WHITE}
              />
            </CollapseButton>
          ) : (
            <ExpandButton
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-label="Expand new homes content"
            >
              <SvgIcon
                name={SvgNames.ArrowsOutSimple}
                width={18}
                height={18}
                color={tokens.WHITE}
              />
            </ExpandButton>
          )}
          {isInlineLoading && (
            <LoaderWrapper>
              <LoadingIndicator color="black" />
            </LoaderWrapper>
          )}
          <InlineIframe
            isExpanded={isExpanded}
            src={IFRAME_SRC}
            title="New homes sales proposal"
            allow="fullscreen;"
            loading="lazy"
            onLoad={() => setIsInlineLoading(false)}
          />
        </IframeCard>
      </SectionContainer>
    </>
  );
};

const SectionContainer = styled.section`
  width: 986px;
  margin: 0 auto 64px;
  padding: 0 16px;
  box-sizing: border-box;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    width: 100%;
    padding: 0 48px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    padding: 0 16px;
  }
`;

const Overlay = styled.button`
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 11;
  cursor: pointer;
  padding: 0;
`;

const IframeCard = styled.div<{ isExpanded: boolean }>`
  position: relative;
  width: 100%;
  overflow: visible;

  ${({ isExpanded }) =>
    isExpanded &&
    `
      position: fixed;
      top: 16px;
      right: 16px;
      bottom: 16px;
      left: 16px;
      width: 986px;
      height: 740px;
      margin: auto;
      z-index: 15;

      @media (max-width: ${TABLET_BREAKPOINT}px) {
        width: calc(100% - 32px);
        height: min(calc(100% - 32px), 740px);
      }
    `}
`;

const InlineIframe = styled.iframe<{ isExpanded: boolean }>`
  display: block;
  width: 100%;
  height: ${({ isExpanded }) => (isExpanded ? "100%" : "715px")};
  border: 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    height: ${({ isExpanded }) => (isExpanded ? "100%" : "496px")};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${({ isExpanded }) => (isExpanded ? "100%" : "264px")};
  }
`;

const ExpandButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${tokens.BRAND_HERO_BLUE};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 1;

  &:hover {
    opacity: 0.9;
  }
`;

const CollapseButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;

  &:hover {
    opacity: 0.9;
  }
`;

const LoaderWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export { NewHomesExpandableIframe };
